import React from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import LoginView from './login/LoginView';
import LiveView from './live/LiveView';
import MCPlaybackView from './mcplayback/MCPlaybackView';
import SetupView from './setup/SetupView';
import Error from './PageError';
import { connect } from 'react-redux';
import { getServer, checkExists,logoutUser, expireSession, getPlatform, getBrowser, resumePortalSessionRefresh, refreshCloudAccess, refreshServerVersion, applyCloudApiVersion } from './actions';
import { clearJumpedServerUrl } from './util/getServerInfo';
import { debugLog, debugWarn } from './util/debug';
import { initTheme } from './util/theme';
import { initCamTheme } from './util/camTheme';
import { persistor } from './store';
import { getProductConfig, getBackend } from './backend';
import { CLOUDAPI_VERSION_EVENT } from './backend/veCall';
import UpdateBanner from './common/UpdateBanner';
import './App.css';

// How often the session poll also re-reads the NVR's DVS version, so a tab left
// open across an upgrade stops showing the old release.
export const SERVER_VERSION_REFRESH_MS = 5 * 60 * 1000;

// this component will also institute a timer to check for inactivity 
// the timer will be set to the users bInactivityTimeout value
// the timer will be decremented every second as long as the user has not interacted with the app
// if the user does interact with the app before our countdown reaches 0 then we should reset the timer back to the users bInactivityTimeout value
// if the user does not interact with the app before our countdown reaches 0 then we should log them out
// the timer should only be running when the user is logged in and the users bInactivity timeout value is not 0
// the timer value should be reset to the users bInactivity timer that has logged in, upon logout the timer is set to null and we do not attempt to count down

class App extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
        temp: ''
    };

    this.verifySessionHandler = null;
    this.lastServerVersionRefresh = Date.now(); // getServer reads it on load
    this.inactivityTimer = null;
    this.inactivityHandler = null;
  };

  componentDidMount = () => {
    // Initialize theme from localStorage / browser preference
    initTheme();
    // Initialize MCPlayback camera-color prefs (intensity + colorblind-safe)
    initCamTheme();

    // Startup cleanup: clear session-scoped state if user is not logged in.
    // This prevents stale jumpedServerUrl or eviewMode from a previous session
    // (e.g., browser closed without logout) from hijacking API routing on fresh load.
    const hasSession = localStorage.getItem('sSess');
    if (!hasSession) {
      clearJumpedServerUrl();
      localStorage.removeItem('eviewMode');
    }

    this.props.getPlatform();
    this.props.getBrowser();

    // Local NVR product only: validate/refresh the session against the serving
    // NVR (getServer / checkExists / the verifySession poll). The cloud product's
    // session is the cloudapi JWT — there is no local NVR to poll, and polling one
    // would trip noauth→EXPIRE_SESSION and bounce the cloud session.
    if (getProductConfig().hasLocalNvrSession) {
      // If Redux state was rehydrated with an active session (page refresh while
      // logged in), validate it's still alive before trusting the persisted state.
      if (this.props.isLoggedIn && this.props.sSess) {
        this.validateRehydratedSession();
      } else {
        // No rehydrated session — normal fresh load
        this.props.getServer();
      }
      this.verifySession();
    } else {
      // Cloud product: no local NVR to poll. If a PORTAL Microsoft OAuth session
      // was rehydrated (the adapter holds a refresh token), restart the refresh
      // loop — otherwise the 1-hour cloud JWT is never re-minted and the session
      // silently dies within an hour of a page reload. No-op for password cloud
      // login (no refresh token).
      this.props.resumePortalSessionRefresh();
    }

    this.addEventListeners();
    window.addEventListener(CLOUDAPI_VERSION_EVENT, this.onCloudApiVersion);

    // Cloud: a device call refused with a 403 refreshes the session's access once and retries
    // (CloudNvrAdapter._deviceCall; #86bc6dkfa). No-op for the local NVR adapter.
    try {
      const backend = getBackend();
      if (backend && typeof backend.setAccessRefresher === 'function') {
        backend.setAccessRefresher(() => this.props.refreshCloudAccess({ force: true }));
      }
    } catch (e) { /* a product with no adapter yet */ }
  }

  onCloudApiVersion = (event) => {
    this.props.applyCloudApiVersion(event.detail);
  }

  // Back to this tab or window: pick up devices granted or removed meanwhile (refreshCloudAccess
  // throttles this to once a minute and is a no-op outside the cloud product).
  refreshOnReturn = () => {
    if (document.visibilityState === 'visible' && this.props.isLoggedIn) this.props.refreshCloudAccess();
  }

  /**
   * Validate a rehydrated session after page refresh.
   * If the session is still valid on the server, the user stays on their current route
   * with all their cameras, views, and state intact.
   * If invalid, purge persisted state and redirect to login.
   */
  validateRehydratedSession = async () => {
    try {
      const isValid = await this.props.checkExists(this.props.sSess);
      if (isValid) {
        // Session is alive — user stays where they are.
        // Refresh server data in background to catch any config changes.
        this.props.getServer();
      } else {
        // Session expired/invalid — clean up and go to login
        debugLog('[App] Rehydrated session invalid, redirecting to login');
        persistor.purge();
        this.props.expireSession(this.props.sSess);
      }
    } catch (e) {
      // Network error — can't validate. Clear session to be safe.
      debugWarn('[App] Session validation failed:', e);
      persistor.purge();
      this.props.expireSession(this.props.sSess);
    }
  }

  componentWillUnmount = () => {
    clearTimeout(this.verifySessionHandler);
    clearInterval(this.inactivityHandler);
    this.removeEventListeners();
    window.removeEventListener(CLOUDAPI_VERSION_EVENT, this.onCloudApiVersion);
    // The adapter must not keep calling back into an unmounted App (review L11, #86bc6dkfa).
    try {
      const backend = getBackend();
      if (backend && typeof backend.setAccessRefresher === 'function') backend.setAccessRefresher(null);
    } catch (e) { /* a product with no adapter */ }
  };

  componentDidUpdate = (prevProps) => {
    // (Re)start session verification when the user logs IN; stop it when they log
    // OUT. verifySession is self-perpetuating via timers once started, so this is
    // what keeps the "logged out polls nothing" invariant across a session's life.
    if(getProductConfig().hasLocalNvrSession && this.props.isLoggedIn && !prevProps.isLoggedIn) {
      this.verifySession();
    }
    if(!this.props.isLoggedIn && prevProps.isLoggedIn) {
      clearTimeout(this.verifySessionHandler);
      this.verifySessionHandler = null;
    }
    // if the user is logged in and the inactivity timeout value is not 0 then we need to start the inactivity timer
    if(this.props.bInactivityTimeout !== prevProps.bInactivityTimeout) {
      if(this.props.bInactivityTimeout > 0) {
        this.inactivityTimer = this.props.bInactivityTimeout * 60;
        this.startInactivityHandler();
      } else {
        this.inactivityTimer = null;
        clearInterval(this.inactivityHandler);
        this.inactivityHandler = null;
      }
    }
  }

  // Session verification runs ONLY while logged in. Logged out ⇒ poll nothing:
  // no checkExists, no re-armed timer. The poll (re)starts from componentDidUpdate
  // when the user logs in. (Previously a half-applied guard — it only tripped when
  // verifySession was called WITH an arg, but its own re-arm timers called it with
  // none — so it kept hitting the NVR every ~3-5 min after logout.)
  verifySession = async() => {
    if(!this.props.isLoggedIn || !this.props.sSess) {
      clearTimeout(this.verifySessionHandler);
      this.verifySessionHandler = null;
      return;
    }
    try {
      const exists = await this.props.checkExists(this.props.sSess);
      if(exists === false) {
        // Definitive invalid session — expire (flips isLoggedIn false so the next
        // tick short-circuits) and stop polling.
        persistor.purge(); // Clear persisted state so stale session doesn't rehydrate
        this.props.expireSession(this.props.sSess);
        clearTimeout(this.verifySessionHandler);
        this.verifySessionHandler = null;
        return;
      }
      // Still valid — re-check in 60s.
      clearTimeout(this.verifySessionHandler);
      this.verifySessionHandler = setTimeout( () => this.verifySession(), 60000 );
      // The session outlives an NVR upgrade, and getServer ran only on load.
      if (Date.now() - this.lastServerVersionRefresh >= SERVER_VERSION_REFRESH_MS) {
        this.lastServerVersionRefresh = Date.now();
        this.props.refreshServerVersion();
      }
    } catch (error) {
      // Server unreachable is NOT a definitive expiry (no spurious logout on a
      // transient blip). Back off and retry in 5 min — still logged in.
      debugLog('Session verification failed:', error);
      clearTimeout(this.verifySessionHandler);
      this.verifySessionHandler = setTimeout( () => this.verifySession(), 300000 );
    }
  };

  // when the user interacts with our web page we should reset the inactivity timer back to its default value (users bInactivityTimeout * 60)
  resetInactivityTimer = () => {
    if(this.props.bInactivityTimeout > 0) {
      clearInterval(this.inactivityHandler);
      this.inactivityTimer = this.props.bInactivityTimeout * 60;
      this.inactivityHandler = null;
      this.startInactivityHandler();
    }
  };

  debounce = (func, delay) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  };

  debouncedResetInactivityTimer = this.debounce(this.resetInactivityTimer, 300);

  // add event listeners to the document to listen for user interaction
  // when the user interacts with the document we should reset the inactivity timer
  addEventListeners = () => {
    // how do we limit multiple fast clicks and key presses so we don't call resetInactivityTimer multiple times
    document.addEventListener('mousemove', this.debouncedResetInactivityTimer);
    document.addEventListener('keydown', this.debouncedResetInactivityTimer);
    document.addEventListener('scroll', this.debouncedResetInactivityTimer);
    window.addEventListener('touchstart', this.debouncedResetInactivityTimer);
    window.addEventListener('focus', this.refreshOnReturn);
    document.addEventListener('visibilitychange', this.refreshOnReturn);
  }

  startInactivityHandler = () => {
    // Clear any interval already running before re-arming. componentDidUpdate calls this on a
    // bInactivityTimeout change WITHOUT clearing first — without this guard the prior interval is
    // orphaned and both decrement the shared countdown → auto-logout fires at ~half the set duration.
    clearInterval(this.inactivityHandler);
    this.inactivityHandler = setInterval( () => {
      // if we are logged in and the user has an inactivity timeout value, then we need to start the inactivity timer
      if(this.props.isLoggedIn && this.props.bInactivityTimeout && this.props.bInactivityTimeout > 0) {
        if(this.inactivityTimer > 0) {
          this.inactivityTimer--;
        } else {
          // we have reached the end of our inactivity timer, so we need to log the user out
          persistor.purge(); // Clear persisted state on inactivity logout
          this.props.logoutUser(this.props.sSess, 'inactive');
          clearInterval(this.inactivityHandler);
          this.inactivityHandler = null;
        }
      } else {
        // we are logged out, so we don't want to update anything but we want to keep the timer running
        this.inactivityHandler = null;
        this.inactivityTimer = null;
      }
    }, 1000);
  }

  removeEventListeners = () => {
    document.removeEventListener('mousemove', this.debouncedResetInactivityTimer);
    document.removeEventListener('keydown', this.debouncedResetInactivityTimer);
    document.removeEventListener('scroll', this.debouncedResetInactivityTimer);
    window.removeEventListener('touchstart', this.debouncedResetInactivityTimer);
    window.removeEventListener('focus', this.refreshOnReturn);
    document.removeEventListener('visibilitychange', this.refreshOnReturn);
    clearInterval(this.inactivityHandler);
  }

  render() {

    return (
      <div className="App">
        <UpdateBanner />
        <Switch>
          <Route path="/live" >
            { !(this.props.isLoggedIn && this.props.sSess)
              ? <Redirect to='/' />
              : <LiveView />
            }
          </Route>
          <Route path="/mcplayback" >
            { !(this.props.isLoggedIn && this.props.sSess)
              ? <Redirect to='/' />
              : <MCPlaybackView />
            }
          </Route>
          <Route path="/setup" >
            { !(this.props.isLoggedIn && this.props.sSess)
              ? <Redirect to='/' />
              : <SetupView />
            }
          </Route>
          <Route exact path="/" >
            {/* Stay signed in: a rehydrated valid session (redux-persist keeps
                isLoggedIn + sSess) resumes straight to the viewer instead of
                showing the login screen when the user reopens the base URL.
                componentDidMount's validateRehydratedSession has already fired
                checkExists; an invalid session flips isLoggedIn false and this
                falls back to LoginView. Mirrors dview's launch-resume (ADR-047).
                Applies to every login type (password + OAuth). */}
            { this.props.isLoggedIn && this.props.sSess
              ? <Redirect to='/live' />
              : <LoginView />
            }
          </Route>
          <Route>
            <Error />
          </Route>
        </Switch>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const { sSess, isLoggedIn } = state.auth;
  const { sServer } = state.server;
  const { fFullscreen } = state.video;
  const { bInactivityTimeout } = state.user
  return {
    sSess,
    isLoggedIn,
    sServer,
    fFullscreen,
    bInactivityTimeout,
  }
}
export default connect( mapStateToProps, { getServer, checkExists, logoutUser, expireSession, getPlatform, getBrowser, resumePortalSessionRefresh, refreshCloudAccess, refreshServerVersion, applyCloudApiVersion })(App);